/**
 * Gallery State Module
 *
 * Reactive Svelte 5 state for managing favorited images.
 * Uses $state() runes for reactivity and integrates with IndexedDB via gallery-db.js.
 *
 * Architecture:
 * - galleryState: Reactive state object
 * - galleryDB: Pure IndexedDB operations (boundary layer)
 * - $state.snapshot(): Used to serialize proxies before IndexedDB writes
 */

import * as galleryDB from '../db/gallery-db.js';
import { getAccountSnapshot } from '../sogni-client.svelte.js';
import { createFavoriteVideoStorage } from '../utils/favorite-videos.js';

// Friendly message shown when the browser's storage quota is exhausted.
const QUOTA_ERROR_MESSAGE = 'Device storage is full — remove some favorites to save new ones';

/**
 * Detect a storage-quota-exceeded error.
 * Native IndexedDB throws a DOMException named 'QuotaExceededError'; Dexie
 * wraps the underlying error, so the real cause may be on error.inner.
 *
 * @param {any} err - The thrown error
 * @returns {boolean} True if this is a quota-exceeded error
 */
function isQuotaExceededError(err) {
  return (
    err?.name === 'QuotaExceededError' ||
    err?.inner?.name === 'QuotaExceededError'
  );
}

// Main gallery state with $state() for reactivity
export const galleryState = $state({
  favorites: [],
  favoriteIds: new Set(),  // For O(1) lookup
  selectedIds: new Set(),  // For multi-select in Gallery
  isLoading: true,
  error: null
});

// Surface IndexedDB versionchange (another tab upgraded the DB) as a
// non-blocking error instead of forcing a reload (which kills session state).
galleryDB.setVersionChangeHandler((message) => {
  galleryState.error = message;
});

// URLs are page-local; completed video bytes are stored per account in IndexedDB.
export const sessionVideos = $state({});
export const videoStorageStatus = $state({ saved: {}, errors: {}, sensitive: {} });
const videoStorage = createFavoriteVideoStorage({
  urls: sessionVideos, status: videoStorageStatus,
  getAccountId: () => {
    const account = getAccountSnapshot();
    return account.authenticated ? account.walletAddress?.toLowerCase() || null : null;
  },
  isFavorite: (id) => galleryState.favoriteIds.has(id),
  load: galleryDB.loadVideos, save: galleryDB.saveVideo, remove: galleryDB.deleteVideo,
  fetchBlob: fetchImageAsBlob
});

export async function loadFavoriteVideos(accountId) {
  await loadFavorites();
  return videoStorage.hydrate(accountId);
}
export function clearLoadedVideos() { videoStorage.clear(); }
export function retryUnsavedVideos() {
  const account = getAccountSnapshot();
  const accountId = account.authenticated ? account.walletAddress?.toLowerCase() : null;
  return Promise.all(Object.entries(sessionVideos).filter(([id]) => !videoStorageStatus.saved[id])
    .map(([id, url]) => videoStorage.save(id, url, { accountId })));
}

/**
 * Drop selection and session-video entries that no longer belong to a loaded
 * favorite. A successful IndexedDB read is the source of truth; failed reads
 * intentionally keep video URLs so a retry can reconnect them to favorites.
 *
 * @param {Set<string>} validFavoriteIds
 */
function pruneTransientGalleryState(validFavoriteIds) {
  galleryState.selectedIds = new Set(
    [...galleryState.selectedIds].filter(id => validFavoriteIds.has(id))
  );

  videoStorage.prune(validFavoriteIds);
}

/**
 * Retain a completed video for its account and favorite, with visible save errors.
 * @param {string} favoriteId - The ID of the favorite
 * @param {string} videoUrl - The video URL
 */
export function setFavoriteVideo(favoriteId, videoUrl, metadata = {}) {
  return videoStorage.save(favoriteId, videoUrl, metadata);
}

/**
 * Get the page-local URL for a retained video.
 * @param {string} favoriteId - The ID of the favorite
 * @returns {string|undefined} The video URL or undefined
 */
export function getFavoriteVideo(favoriteId) {
  return sessionVideos[favoriteId];
}

/**
 * Clear the video URL for a favorite
 * @param {string} favoriteId - The ID of the favorite
 */
export function clearFavoriteVideo(favoriteId) {
  return videoStorage.remove(favoriteId);
}

// Favorite count getter for header badge
export const favoriteCount = {
  get value() {
    return galleryState.favorites.length;
  }
};

// Selected count getter for action bar
export const selectedCount = {
  get value() {
    return galleryState.selectedIds.size;
  }
};

/**
 * Toggle selection of a favorite
 * Uses Set reassignment to trigger Svelte 5 reactivity
 *
 * @param {string} id - The ID of the favorite to toggle
 */
export function toggleSelection(id) {
  if (galleryState.selectedIds.has(id)) {
    galleryState.selectedIds.delete(id);
  } else {
    galleryState.selectedIds.add(id);
  }
  // Force reactivity by reassigning (Svelte 5 Set mutation pitfall)
  galleryState.selectedIds = new Set(galleryState.selectedIds);
}

/**
 * Clear all selections
 */
export function clearSelections() {
  galleryState.selectedIds = new Set();
}

/**
 * Replace the current Gallery selection, pruning IDs that no longer exist.
 * Useful after a batch operation so failed items can remain selected for retry.
 *
 * @param {Iterable<string>} ids - Favorite IDs to select
 */
export function setSelections(ids) {
  const favoriteIds = new Set(galleryState.favorites.map((favorite) => favorite.id));
  galleryState.selectedIds = new Set(
    Array.from(ids || []).filter((id) => favoriteIds.has(id))
  );
}

/**
 * Check if an item is selected (sync lookup from Set)
 *
 * @param {string} id - The ID to check
 * @returns {boolean} True if selected
 */
export function isSelected(id) {
  return galleryState.selectedIds.has(id);
}

/**
 * Get all selected favorites (for video generation)
 *
 * @returns {Array} Array of selected favorite objects
 */
export function getSelectedFavorites() {
  return galleryState.favorites.filter(f => galleryState.selectedIds.has(f.id));
}

/**
 * Load all favorites from IndexedDB into state
 * Call this on app initialization
 */
export async function loadFavorites() {
  galleryState.isLoading = true;
  galleryState.error = null;

  try {
    const favorites = await galleryDB.getAllFavorites();

    // Update state
    galleryState.favorites = favorites;
    galleryState.favoriteIds = new Set(favorites.map(f => f.id));
    pruneTransientGalleryState(galleryState.favoriteIds);
  } catch (err) {
    console.warn('[Gallery] Failed to load favorites from IndexedDB:', err);
    galleryState.error = err.message || 'Failed to load favorites';
    // Don't crash - just start with empty favorites
    galleryState.favorites = [];
    galleryState.favoriteIds = new Set();
    // Avoid leaving an actionable selection bar with no loaded favorites. Keep
    // sessionVideos intact because a retry may successfully reload their owners.
    galleryState.selectedIds = new Set();
  } finally {
    galleryState.isLoading = false;
  }
}

/**
 * Add a favorite to state and persist to IndexedDB
 * Uses optimistic update with rollback on error
 *
 * @param {Object} imageData - Image data to save
 * @param {string} [imageData.imageId] - Optional image ID (will generate if missing)
 * @param {Blob} imageData.blob - Image blob data
 * @param {string} [imageData.originalUrl] - Original image URL
 * @param {string} imageData.prompt - Generation prompt
 * @param {string} imageData.model - Model used for generation
 * @param {string} [imageData.requestedModel] - Model selected before any fallback
 * @param {string} [imageData.workerName] - Assigned render worker
 * @param {string} [imageData.projectId] - Sogni project identifier
 * @param {string} [imageData.jobId] - Sogni render job identifier
 * @param {number} [imageData.batchIndex] - Batch index in iteration
 * @param {number} [imageData.imageIndex] - Image index in batch
 */
export async function addFavorite(imageData) {
  // Generate ID if not provided
  const id = imageData.imageId || crypto.randomUUID();

  // Create favorite object
  const favorite = {
    id,
    blob: imageData.blob,
    originalUrl: imageData.originalUrl || imageData.url || null,
    prompt: imageData.prompt || '',
    model: imageData.model || '',
    requestedModel: imageData.requestedModel || imageData.model || '',
    workerName: imageData.workerName || null,
    projectId: imageData.projectId || null,
    jobId: imageData.jobId || null,
    requestId: imageData.requestId || null,
    seed: imageData.seed ?? null,
    width: imageData.width ?? null,
    height: imageData.height ?? null,
    steps: imageData.steps ?? null,
    guidance: imageData.guidance ?? null,
    sampler: imageData.sampler || null,
    scheduler: imageData.scheduler || null,
    completedAt: imageData.completedAt ?? null,
    batchIndex: imageData.batchIndex ?? null,
    imageIndex: imageData.imageIndex ?? null,
    // Server label on the delivered render; covers the favorite until revealed.
    sensitive: imageData.sensitive === true,
    savedAt: Date.now()
  };

  // Optimistic update
  galleryState.favorites.unshift(favorite);  // Add to beginning (newest first)
  galleryState.favoriteIds.add(id);
  galleryState.favoriteIds = new Set(galleryState.favoriteIds);

  try {
    // CRITICAL: Use $state.snapshot() to serialize proxy before IndexedDB write
    const snapshotFavorite = $state.snapshot(favorite);
    await galleryDB.saveFavorite(snapshotFavorite);
  } catch (err) {
    console.error('[Gallery] Failed to save favorite:', err);

    // Rollback optimistic update
    galleryState.favorites = galleryState.favorites.filter(f => f.id !== id);
    galleryState.favoriteIds.delete(id);
    galleryState.favoriteIds = new Set(galleryState.favoriteIds);

    // Surface a friendly message when the device is out of storage so the
    // caller's toast explains why hearts stopped sticking (instead of failing silently).
    if (isQuotaExceededError(err)) {
      throw new Error(QUOTA_ERROR_MESSAGE);
    }

    throw err;  // Re-throw so caller can handle
  }
}

/**
 * Remove a favorite from state and IndexedDB
 * Uses optimistic update with rollback on error
 *
 * @param {string} id - The ID of the favorite to remove
 */
export async function removeFavorite(id) {
  // Find the favorite before removal (for rollback)
  const favoriteIndex = galleryState.favorites.findIndex(f => f.id === id);
  if (favoriteIndex === -1) {
    console.warn('[Gallery] Favorite not found:', id);
    // Repair any transient state left behind by a previous partial update.
    if (galleryState.selectedIds.delete(id)) {
      galleryState.selectedIds = new Set(galleryState.selectedIds);
    }
    delete sessionVideos[id];
    return;
  }

  const favorite = galleryState.favorites[favoriteIndex];
  const wasSelected = galleryState.selectedIds.has(id);
  const hadSessionVideo = Object.prototype.hasOwnProperty.call(sessionVideos, id);
  const previousSessionVideo = sessionVideos[id];

  // Optimistic update
  galleryState.favorites.splice(favoriteIndex, 1);
  galleryState.favoriteIds.delete(id);
  galleryState.favoriteIds = new Set(galleryState.favoriteIds);
  if (wasSelected) {
    galleryState.selectedIds.delete(id);
    galleryState.selectedIds = new Set(galleryState.selectedIds);
  }
  delete sessionVideos[id];

  try {
    await galleryDB.removeFavorite(id);
    // A generation that completed during the database write must not recreate
    // a video entry for a favorite that no longer exists.
    delete sessionVideos[id];
    videoStorage.prune(galleryState.favoriteIds);
  } catch (err) {
    console.error('[Gallery] Failed to remove favorite:', err);

    // Rollback - restore the favorite at its original position
    galleryState.favorites.splice(favoriteIndex, 0, favorite);
    galleryState.favoriteIds.add(id);
    galleryState.favoriteIds = new Set(galleryState.favoriteIds);
    if (wasSelected) {
      galleryState.selectedIds.add(id);
      galleryState.selectedIds = new Set(galleryState.selectedIds);
    }
    if (hadSessionVideo && !Object.prototype.hasOwnProperty.call(sessionVideos, id)) {
      sessionVideos[id] = previousSessionVideo;
    }

    throw err;  // Re-throw so caller can handle
  }
}

/**
 * Check if an image is favorited (sync lookup from Set)
 * Use this for UI rendering - it's O(1) and synchronous
 *
 * @param {string} imageId - The image ID to check
 * @returns {boolean} True if favorited
 */
export function isFavorited(imageId) {
  return galleryState.favoriteIds.has(imageId);
}

/**
 * Fetch an image as a blob from URL
 *
 * @param {string} url - The image URL to fetch
 * @returns {Promise<Blob>} The image blob
 */
export async function fetchImageAsBlob(url) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
  }

  return await response.blob();
}

/**
 * Favorite an image from URL (main entry point from UI)
 * Fetches the image as blob and saves to favorites
 *
 * @param {Object} imageData - Image data with URL
 * @param {string} imageData.url - Image URL to fetch
 * @param {string} [imageData.imageId] - Optional image ID
 * @param {string} imageData.prompt - Generation prompt
 * @param {string} imageData.model - Model used
 * @param {string} [imageData.requestedModel] - Model selected before any fallback
 * @param {string} [imageData.workerName] - Assigned render worker
 * @param {string} [imageData.projectId] - Sogni project identifier
 * @param {string} [imageData.jobId] - Sogni render job identifier
 * @param {number} [imageData.batchIndex] - Batch index
 * @param {number} [imageData.imageIndex] - Image index
 */
export async function favoriteImage(imageData) {
  if (!imageData.url) {
    throw new Error('Image URL is required');
  }

  // Fetch the image as blob
  const blob = await fetchImageAsBlob(imageData.url);

  // Save with blob
  await addFavorite({
    ...imageData,
    blob,
    originalUrl: imageData.url
  });
}

/**
 * Request persistent storage from the browser
 * Call this early in app lifecycle to prevent Safari from evicting data
 *
 * @returns {Promise<boolean>} True if granted, false if denied or unsupported
 */
export async function requestPersistentStorage() {
  if (!navigator.storage?.persist) {
    console.warn('[Gallery] Persistent storage API not supported');
    return false;
  }

  try {
    const granted = await navigator.storage.persist();

    if (granted) {
      console.log('[Gallery] Persistent storage granted');
    } else {
      console.warn('[Gallery] Persistent storage denied - Safari may evict data after 7 days');
    }

    return granted;
  } catch (err) {
    console.warn('[Gallery] Failed to request persistent storage:', err);
    return false;
  }
}
